import React, { useContext } from 'react';
import { createContext, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { FieldSchema, FormBuilderSchema, mapFormSchema, SectionSchema } from './formElements/formBuilderSchema';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { useCurrentUserProfile } from '../../../app/hooks/useCurrentUserProfile';
import { useStoreActions, useStoreState } from '../../../models/hooks';
import { formBuilderSchemas } from './formBuilderSchemas/formBuilder.schema';
import { PATH_ADMINISTRATION_TEMPLATES, PATH_HIRE_FORM_EDIT } from '../../../routes/constants';
import { useTranslation } from 'react-i18next';
import { useExitPrompt } from '../companyManagement/useExitPrompt';

interface FormBuilderContext {
  formSchema: SectionSchema[];
  setFormSchema: (value: SectionSchema[]) => void;
  languages: string[];
  setLanguages: React.Dispatch<React.SetStateAction<string[]>>;
  language: string;
  setLanguage: React.Dispatch<React.SetStateAction<string>>;
  defaultLanguage: string;
  setDefaultLanguage: React.Dispatch<React.SetStateAction<string>>;
  templateName: string;
  setTemplateName: React.Dispatch<React.SetStateAction<string>>;
  findIndexById: (id: string) => number;
  draggedElement: FormBuilderSchema | null;
  setDraggedElement: (element: FormBuilderSchema | null) => void;
  setShowExitPrompt: React.Dispatch<React.SetStateAction<boolean>>;
}

const HireFormContext = createContext<FormBuilderContext>({
  formSchema: [],
  setFormSchema: (o) => o,
  languages: ['en', 'fr'],
  setLanguages: (o) => o,
  language: 'en',
  defaultLanguage: 'en',
  setDefaultLanguage: (o) => o,
  setLanguage: (o) => o,
  templateName: '',
  setTemplateName: (o) => o,
  findIndexById: () => -1,
  draggedElement: null,
  setDraggedElement: (o) => o,
  setShowExitPrompt: () => {},
});

const HireFormProvider: React.FC = ({ children }) => {
  const [languages, setLanguages] = useState<string[]>([]);
  const { i18n } = useTranslation();
  const [defaultLanguage, setDefaultLanguage] = useState<string>('en');
  const history = useHistory();
  const originalTemplate = React.useRef<SectionSchema[] | null>(null);
  const { getTemplateById, setCurrentTemplate, setErrors } = useStoreActions((state) => state.hrFormTemplate);
  const { currentTemplate } = useStoreState((state) => state.hrFormTemplate);
  const [formSchema, setFormSchema] = useState<SectionSchema[]>([{ section: 'section1', fields: [], id: uuidv4() }]);
  const [language, setLanguage] = useState<string>(languages[0]);
  const [templateName, setTemplateName] = useState<string>('');
  const [draggedElement, setDraggedElement] = useState<FormBuilderSchema | null>(null);
  let templateId = useParams<{ templateId?: string }>()?.templateId ?? undefined;
  const isEdit = useRouteMatch(PATH_HIRE_FORM_EDIT);
  const { currentUserProfile } = useCurrentUserProfile();
  const accountId = currentUserProfile?.account ?? '';
  const location = useLocation();

  React.useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const defaultLang = (queryParams.get('defaultLanguage') as string) || i18n.language;
    const availableLanguages = queryParams.get('languages') || i18n.language;
    if (defaultLang) {
      setDefaultLanguage(defaultLang as string);
      setLanguage(defaultLang as string);
    }
    if (availableLanguages) {
      setLanguages(availableLanguages.split(',') as string[]);
    }
    // eslint-disable-next-line
  }, [i18n]);
  const initialSection = [{ section: 'section1', fields: [], id: uuidv4() }];

  React.useEffect(
    () => {
      setErrors([]);
    },
    // eslint-disable-next-line
    [],
  );
  const [, setShowExitPrompt] = useExitPrompt(false);
  React.useEffect(() => {
    const hasChanged = JSON.stringify(originalTemplate.current) !== JSON.stringify(formSchema);
    setShowExitPrompt(hasChanged);
  }, [formSchema, originalTemplate, setShowExitPrompt]);

  React.useEffect(() => {
    if (templateId) {
      getTemplateById({ account: accountId, id: templateId, history });
    } else {
      setCurrentTemplate(undefined);
      handleSetFormSchema(initialSection);
      originalTemplate.current = initialSection;
    }
    // eslint-disable-next-line
  }, [templateId]);

  React.useEffect(() => {
    if (isEdit && currentTemplate?.type === 'default') {
      history.replace(PATH_ADMINISTRATION_TEMPLATES);
      return;
    }
    if (!currentTemplate?.questions?.length || !templateId) {
      handleSetFormSchema(initialSection);
      originalTemplate.current = initialSection;
      return;
    }
    if (currentTemplate.languages) {
      setLanguages(currentTemplate.languages);
    }
    if (currentTemplate.defaultLanguage) {
      setDefaultLanguage(currentTemplate.defaultLanguage);
      setLanguage(currentTemplate.defaultLanguage);
    }
    const grouped = mapFormSchema({
      currentTemplate: currentTemplate.questions,
      formBuilderSchemas,
    });

    handleSetFormSchema(grouped);
    originalTemplate.current = grouped;
    // eslint-disable-next-line
  }, [currentTemplate, templateId]);
  function findIndexById(id: string) {
    return formSchema.flatMap((group) => group.fields).findIndex((group) => group.id === id);
  }

  function handleDraggedElement(type: FormBuilderSchema | null) {
    setDraggedElement(type);
  }

  function updateFieldNamesWithIndex(sections: SectionSchema[]): SectionSchema[] {
    const nameMap: Map<string, FieldSchema[]> = new Map();
    const maxIndexMap: Map<string, number> = new Map();

    function isSectionSchema(item: FieldSchema | SectionSchema): item is SectionSchema {
      return 'fields' in item;
    }

    function collectFields(items: (FieldSchema | SectionSchema)[]): void {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (isSectionSchema(item)) {
          collectFields(item.fields);
        } else {
          const match = item.field.match(/^(.+?)(?:_(\d+))?$/);
          if (!match) continue;

          const baseName = match[1];
          const suffix = match[2] ? parseInt(match[2], 10) : null;

          if (suffix === null) {
            if (!nameMap.has(baseName)) {
              nameMap.set(baseName, []);
            }
            const arr = nameMap.get(baseName)!;
            arr.push(item);
          } else {
            const currentMax = maxIndexMap.get(baseName) || 0;
            maxIndexMap.set(baseName, Math.max(currentMax, suffix));
          }

          if (item.followUpQuestionList) {
            collectFields(item.followUpQuestionList);
          }
        }
      }
    }

    function renameDuplicates(): void {
      nameMap.forEach((fields, baseName) => {
        if (fields.length <= 1) return;

        fields.sort((a, b) => a.timestamp - b.timestamp);

        for (let i = 1; i < fields.length; i++) {
          const nextIndex = (maxIndexMap.get(baseName) || 0) + 1;
          fields[i].field = `${baseName}_${nextIndex}`;
          maxIndexMap.set(baseName, nextIndex);
        }
      });
    }

    function rebuildSections(sections: SectionSchema[]): SectionSchema[] {
      const result: SectionSchema[] = [];

      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];
        const updatedFields: (FieldSchema | SectionSchema)[] = [];

        for (let j = 0; j < section.fields.length; j++) {
          const field = section.fields[j];

          if (isSectionSchema(field)) {
            updatedFields.push({
              ...field,
              fields: rebuildFields(field.fields),
            });
          } else {
            updatedFields.push({
              ...field,
              followUpQuestionList: field.followUpQuestionList
                ? (rebuildFields(field.followUpQuestionList) as FieldSchema[])
                : null,
            });
          }
        }

        result.push({
          ...section,
          fields: updatedFields,
        });
      }

      return result;
    }

    function rebuildFields(fields: (FieldSchema | SectionSchema)[]): (FieldSchema | SectionSchema)[] {
      const result: (FieldSchema | SectionSchema)[] = [];

      for (let i = 0; i < fields.length; i++) {
        const item = fields[i];

        if (isSectionSchema(item)) {
          result.push({
            ...item,
            fields: rebuildFields(item.fields),
          });
        } else {
          result.push({
            ...item,
            followUpQuestionList: item.followUpQuestionList ? (rebuildFields(item.followUpQuestionList) as FieldSchema[]) : null,
          });
        }
      }

      return result;
    }

    collectFields(sections);
    renameDuplicates();
    return rebuildSections(sections);
  }

  function addParentToFollowUps(sections: SectionSchema[]): SectionSchema[] {
    const handleFields = (fields: (FieldSchema | SectionSchema)[]): (FieldSchema | SectionSchema)[] => {
      return fields.map((item) => {
        if ('field' in item) {
          // It's a FieldSchema
          const newItem: FieldSchema = { ...item };
          if (!newItem.isFollowUp) {
            newItem.followUpField = null;
          }

          if (newItem.followUpQuestionList) {
            newItem.followUpQuestionList = newItem.followUpQuestionList.map((fuQ) => ({
              ...fuQ,
              parent: newItem.id,
            }));
            // Recursively process follow-ups
            newItem.followUpQuestionList = handleFields(newItem.followUpQuestionList) as FieldSchema[];
            // Set followUpField for single follow-up
            if (newItem.followUpQuestionList.length === 1) {
              newItem.followUpField = newItem.followUpQuestionList[0].field;
            }
          }

          return newItem;
        } else {
          // It's a nested SectionSchema
          return {
            ...item,
            fields: handleFields(item.fields),
          };
        }
      });
    };

    return sections.map((section) => ({
      ...section,
      fields: handleFields(section.fields),
    }));
  }

  const updateFieldValue = (newSchemaAttempt: SectionSchema[], currentState: SectionSchema[]): SectionSchema[] => {
    const isValid = newSchemaAttempt.every((section) => section.id && section.id.trim() !== '');
    if (!isValid) {
      console.warn('PROVIDER: Validation failed. Update prevented. Keeping current state.');
      alert('PROVIDER Validation: Title is required for all sections!');
      return currentState;
    }

    return addParentToFollowUps(updateFieldNamesWithIndex(newSchemaAttempt));
  };

  const handleSetFormSchema = (data: SectionSchema[]) => {
    const processedState = updateFieldValue(data, originalTemplate.current ?? []);
    originalTemplate.current = processedState;

    setFormSchema(processedState);
  };

  return (
    <HireFormContext.Provider
      value={{
        formSchema,
        setFormSchema: handleSetFormSchema,
        languages,
        setLanguages,
        language,
        setLanguage,
        defaultLanguage,
        setDefaultLanguage,
        templateName,
        setTemplateName,
        findIndexById,
        draggedElement,
        setDraggedElement: handleDraggedElement,
        setShowExitPrompt,
      }}
    >
      {children}
    </HireFormContext.Provider>
  );
};

export { HireFormProvider, HireFormContext };

export const useHireFormContext = () => useContext(HireFormContext);
